/**
 * Organized Figma Design Tokens
 * Based on extracted colors from your Figma design
 *
 * Key colors identified from your design:
 * - Primary Red: #d22730 (Biamp brand)
 * - Primary Blue: #1863d3 (action/links)
 * - Text Primary: #111111
 * - Text Secondary: #878787
 * - Background: #f5f5f5, #e0e0e0
 * - Dark Sidebar: #222222
 * - Success: #008a05
 * - Warning: #e06c00
 * - Error: #e0002d, #ff1744
 */

export interface ColorScale {
  main: string;
  light: string;
  dark: string;
  contrast: string;
}

export interface NeutralScale {
  0: string;
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

export interface SemanticColors {
  success: string;
  successLight: string;
  warning: string;
  warningLight: string;
  error: string;
  errorBright: string;
  errorDark: string;
  info: string;
  draft: string;
}

export interface SurfaceColors {
  background: string;
  backgroundAlt: string;
  card: string;
  sidebar: string;
  modal: string;
  overlay: string;
  input: string;
  inputBorder: string;
  tableBorder: string;
  tableHeader: string;
}

export interface TextColors {
  primary: string;
  secondary: string;
  disabled: string;
  inverse: string;
  inverseMuted: string;
  link: string;
  error: string;
  success: string;
}

export interface ComponentColors {
  button: {
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryBorder: string;
    disabled: string;
    disabledText: string;
  };
  checkbox: {
    checked: string;
    unchecked: string;
    border: string;
  };
  badge: {
    error: string;
    warning: string;
    success: string;
    info: string;
  };
  navigation: {
    sidebar: string;
    activeItem: string;
    inactiveText: string;
  };
}

export interface FigmaTokens {
  colors: {
    primary: ColorScale;
    secondary: ColorScale;
    neutral: NeutralScale;
    semantic: SemanticColors;
    surface: SurfaceColors;
    text: TextColors;
    components: ComponentColors;
  };
  typography: {
    fontFamily: {
      primary: string;
      display: string;
      mono: string;
    };
    fontSize: {
      xs: string;
      sm: string;
      base: string;
      lg: string;
      xl: string;
      '2xl': string;
      '3xl': string;
      '4xl': string;
    };
    fontWeight: {
      light: number;
      normal: number;
      medium: number;
      semibold: number;
      bold: number;
    };
    lineHeight: {
      tight: number;
      normal: number;
      relaxed: number;
    };
  };
  spacing: Record<string, string>;
  borderRadius: {
    none: string;
    sm: string;
    base: string;
    md: string;
    lg: string;
    xl: string;
    full: string;
  };
  shadows: string[];
  zIndex: {
    base: number;
    dropdown: number;
    sticky: number;
    fixed: number;
    modalBackdrop: number;
    modal: number;
    popover: number;
    tooltip: number;
  };
}

export const figmaOrganizedTokens: FigmaTokens = {
  // COLOR PALETTE - Organized from your Figma extraction
  colors: {
    // Primary Action Colors (Blue for primary actions like New button)
    primary: {
      main: '#1863d3',      // Action blue - primary buttons, links, checkboxes
      light: '#4a8df8',     // Lighter variant
      dark: '#0e3d82',      // Darker variant
      contrast: '#ffffff',  // Text on primary
    },

    // Secondary/Brand Colors (Biamp Red)
    secondary: {
      main: '#d22730',      // Biamp red - brand/logo color
      light: '#ff5757',
      dark: '#9c1e24',
      contrast: '#ffffff',
    },

    // Neutral/Gray Scale (from your design)
    neutral: {
      0: '#ffffff',         // Pure white
      50: '#f5f5f5',       // Background light
      100: '#e0e0e0',      // Borders, dividers
      200: '#c4c4c4',      // Disabled states
      300: '#878787',      // Secondary text, labels
      400: '#61c453',      // Accent (dot indicator)
      500: '#353535',      // Headers
      600: '#343434',      // Segmented buttons
      700: '#222222',      // Sidebar, dark areas
      800: '#1f2224',      // Tab bar dark
      900: '#111111',      // Primary text
      950: '#080808',      // Chrome dark
    },

    // Semantic Colors (from your design)
    semantic: {
      success: '#008a05',     // Green - online status
      successLight: '#61c453', // Light green dot
      warning: '#e06c00',     // Orange - warnings
      warningLight: '#ffc107', // Yellow warning
      error: '#e0002d',       // Red - errors/delete actions
      errorBright: '#ff1744', // Bright red indicator
      errorDark: '#c5002a',   // Dark red for hover
      info: '#1863d3',        // Blue - info (same as primary)
      draft: '#ebf7ff',       // Light blue - draft banner
    },

    // Surface Colors (backgrounds from your design)
    surface: {
      background: '#ffffff',    // Main background
      backgroundAlt: '#f5f5f5', // Alternate background
      card: '#ffffff',          // Card background
      sidebar: '#222222',       // Dark sidebar
      modal: '#ffffff',         // Modal background
      overlay: 'rgba(17, 17, 17, 0.5)', // Modal overlay
      input: '#ffffff',         // Input fields
      inputBorder: '#111111',   // Input borders (when stroked)
      tableBorder: '#111111',   // Table borders
      tableHeader: '#f5f5f5',   // Table header background
    },

    // Text Colors (from your design)
    text: {
      primary: '#111111',       // Primary text
      secondary: '#878787',     // Secondary text, labels
      disabled: '#c4c4c4',      // Disabled text
      inverse: '#ffffff',       // Text on dark backgrounds
      inverseMuted: '#e0e0e0',  // Muted text on dark
      link: '#1863d3',          // Link color
      error: '#e0002d',         // Error text
      success: '#008a05',       // Success text
    },

    // Component-specific colors from your design
    components: {
      button: {
        primary: '#1863d3',          // Primary button
        primaryHover: '#0e3d82',
        secondary: '#ffffff',         // Secondary button
        secondaryBorder: '#111111',   // Button border
        disabled: '#e0e0e0',         // Disabled button
        disabledText: '#878787',
      },
      checkbox: {
        checked: '#1863d3',           // Checkbox checked
        unchecked: '#ffffff',
        border: '#111111',
      },
      badge: {
        error: '#ff1744',             // Error badge
        warning: '#e06c00',           // Warning badge
        success: '#008a05',           // Success badge
        info: '#1863d3',              // Info badge
      },
      navigation: {
        sidebar: '#222222',           // Sidebar background
        activeItem: '#1863d3',        // Active nav item
        inactiveText: '#e0e0e0',     // Inactive nav text
      },
    },
  },

  // TYPOGRAPHY (based on text found in your design)
  typography: {
    fontFamily: {
      primary: "'Open Sans', sans-serif",    // Body text
      display: "'Montserrat', sans-serif",   // Headers
      mono: "'Roboto Mono', monospace",      // Code
    },

    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
      '4xl': '36px',
    },

    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },

    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // SPACING (standard Material Design spacing)
  spacing: {
    0: '0px',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
  },

  // BORDER RADIUS (from your design elements)
  borderRadius: {
    none: '0px',
    sm: '2px',
    base: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    full: '9999px',
  },

  // SHADOWS (Material Design standard 25-level shadow system)
  shadows: [
    'none',                                            // 0
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)', // 1
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)', // 2
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)', // 3
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)', // 4
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)', // 5
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)', // 6
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)', // 7
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)', // 8
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)', // 9
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)', // 10
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)', // 11
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)', // 12
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)', // 13
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)', // 14
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)', // 15
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)', // 16
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)', // 17
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)', // 18
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)', // 19
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)', // 20
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)', // 21
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)', // 22
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)', // 23
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)', // 24
  ],

  // Z-INDEX layers
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
};

/**
 * Retrieves a token value by dot-separated path.
 *
 * @param path - Dot-separated path into the token tree (e.g. 'colors.primary.main')
 * @returns The token value, or undefined if the path does not exist
 */
export const getToken = (path: string): any => {
  return path.split('.').reduce((obj: any, key) => obj?.[key], figmaOrganizedTokens);
};

/** Valid theme modes */
export type ThemeMode = 'light' | 'dark';

/**
 * Builds a MUI ThemeOptions-compatible object from the Figma tokens for
 * a given color mode.
 *
 * @param mode - 'light' or 'dark'
 * @returns MUI ThemeOptions object
 */
export const createThemeFromFigma = (mode: ThemeMode = 'light'): any => {
  const isDark = mode === 'dark';

  return {
    palette: {
      mode,
      primary: {
        main: getToken('colors.primary.main'),
        light: getToken('colors.primary.light'),
        dark: getToken('colors.primary.dark'),
        contrastText: getToken('colors.primary.contrast'),
      },
      secondary: {
        main: getToken('colors.secondary.main'),
        light: getToken('colors.secondary.light'),
        dark: getToken('colors.secondary.dark'),
        contrastText: getToken('colors.secondary.contrast'),
      },
      error: {
        main: getToken('colors.semantic.error'),
        light: getToken('colors.semantic.errorBright'),
        dark: getToken('colors.semantic.errorDark'),
        contrastText: '#ffffff',
      },
      warning: {
        main: getToken('colors.semantic.warning'),
        light: getToken('colors.semantic.warningLight'),
      },
      success: {
        main: getToken('colors.semantic.success'),
        light: getToken('colors.semantic.successLight'),
      },
      info: {
        main: getToken('colors.semantic.info'),
      },
      grey: figmaOrganizedTokens.colors.neutral,
      background: {
        default: isDark ? getToken('colors.neutral.900') : getToken('colors.surface.background'),
        paper: isDark ? getToken('colors.neutral.700') : getToken('colors.surface.backgroundAlt'),
      },
      text: {
        primary: isDark ? getToken('colors.text.inverse') : getToken('colors.text.primary'),
        secondary: isDark ? getToken('colors.text.inverseMuted') : getToken('colors.text.secondary'),
        disabled: getToken('colors.text.disabled'),
      },
      divider: isDark ? getToken('colors.neutral.600') : getToken('colors.neutral.100'),
    },
    typography: {
      fontFamily: figmaOrganizedTokens.typography.fontFamily.primary,
      fontSize: 14, // MUI expects a number for base fontSize
      fontWeightLight: figmaOrganizedTokens.typography.fontWeight.light,
      fontWeightRegular: figmaOrganizedTokens.typography.fontWeight.normal,
      fontWeightMedium: figmaOrganizedTokens.typography.fontWeight.medium,
      fontWeightBold: figmaOrganizedTokens.typography.fontWeight.bold,
    },
    spacing: (factor: number) => `${factor * 4}px`, // MUI expects a function that returns spacing
    shape: {
      borderRadius: parseInt(getToken('borderRadius.base')),
    },
    shadows: figmaOrganizedTokens.shadows,
    zIndex: figmaOrganizedTokens.zIndex,
    // Add component-specific colors
    components: {
      button: {
        ...figmaOrganizedTokens.colors.components.button,
        // Override secondaryBorder based on theme mode
        secondaryBorder: isDark ? 'rgba(255, 255, 255, 0.40)' : 'rgba(17, 17, 17, 0.40)',
      },
      checkbox: figmaOrganizedTokens.colors.components.checkbox,
      badge: figmaOrganizedTokens.colors.components.badge,
      navigation: figmaOrganizedTokens.colors.components.navigation,
    },
  };
};

export default figmaOrganizedTokens;
